import React, { Fragment, useEffect, useState, useRef } from 'react';
import { useFormikContext } from 'formik';
import { classnames } from 'Utils/classnames/classnames.utils';
import { t } from 'Src/i18n';
import { Title } from 'Components/frameworks/title/Title';
import { Capsule } from 'Components/frameworks/capsule/Capsule';
import { HostedField } from 'Components/widgets/payment-info/payment-form/HostedField';
import { FormikInput } from 'Components/frameworks/formik/FormikInput';
import { useScriptLoader } from 'Src/hooks/use-script-loader/useScriptLoader';
import { useDisclosure } from 'Src/hooks/use-disclosure/useDisclosure';
import { useDispatch } from 'react-redux';
import { endLoading, startLoading } from 'Store/global-loader/globalLoader.action';
import { decode } from 'jsonwebtoken';
import { creditCardExpiryValidator } from './paymentProvidersUtils';
const CYBERSOURCE_FIELD_CARD_NUMBER = 'cybersource-card-number';
const CYBERSOURCE_FIELD_EXPIRY_DATE = 'cybersource-card-expiry';
const CYBERSOURCE_FIELD_CVC = 'cybersource-card-cvc';
type Props = {
  token: PaymentProviderToken;
};
const defaultHostedFieldState = {
  valid: false,
  dirty: false,
  empty: true,
};
export const CyberSourceForm = ({ token }: Props) => {
  const formik = useFormikContext();
  const dispatch = useDispatch();
  const {
    ctx: [
      {
        data: { clientLibrary: scriptUrl, clientLibraryIntegrity: scriptIntegrity },
      },
    ],
  } = decode(token) || {
    ctx: [
      {
        data: {},
      },
    ],
  };
  const { scriptLoaded } = useScriptLoader(scriptUrl, {
    integrity: scriptIntegrity,
    crossorigin: 'anonymous',
  });
  const { isOpen: isFormSubmitted, onOpen: setIsFormSubmitted } = useDisclosure();
  const [creditCardFieldsValidation, setCreditCardFieldsValidation] = useState({
    number: defaultHostedFieldState,
    securityCode: defaultHostedFieldState,
  });
  useEffect(() => {
    formik.isSubmitting && setIsFormSubmitted();
  }, [formik.isSubmitting]);
  const { number: cardNumberValidation, securityCode: securityCodeValidation } =
    creditCardFieldsValidation;
  const hostedFieldsState = Object.keys(creditCardFieldsValidation).reduce((acc, key) => {
    const { dirty: isDirty, valid: isValid, empty: isEmpty } = creditCardFieldsValidation[key];
    const isTouched = isDirty || isFormSubmitted;
    acc[key] = {
      hasError: isTouched && (!isValid || isEmpty),
      errorMessage: isEmpty
        ? t('common.form.errors.requiredField')
        : t('common.form.errors.invalidFieldFormat'),
      isValid: isDirty && isValid,
    };
    return acc;
  }, {});
  const microformInstanceRef = useRef();
  useEffect(() => {
    if (scriptLoaded && token) {
      const options = {
        keyId: token,
        keystore: token,
        container: `#${CYBERSOURCE_FIELD_CARD_NUMBER}`,
        label: '.HostedField-label',
        placeholder: t('common.payment.hipay.placeholderCardNumber'),
        styles: {
          input: {
            'font-size': '14px',
            color: '#000',
          },
          '::placeholder': {
            color: '#9ca3af',
          },
        },
      };
      const form = new window.Flex(token);
      const microform = form.microform(options);
      microformInstanceRef.current = microform;
      [
        {
          type: 'number',
          placeholder: t('common.payment.hipay.placeholderCardNumber'),
          id: CYBERSOURCE_FIELD_CARD_NUMBER,
        },
        {
          type: 'securityCode',
          placeholder: t('common.payment.hipay.placeholderCvc'),
          id: CYBERSOURCE_FIELD_CVC,
        },
      ].map(({ type, placeholder, id }) => {
        const field = microform.createField(type, {
          placeholder,
        });
        field.load(`#${id}`);
        field.on('change', ({ valid, card, empty }) => {
          if (card?.[0]?.name) {
            formik.setFieldValue('paymentProduct', card[0]?.name);
          }

          setCreditCardFieldsValidation((validation) => ({
            ...validation,
            [type]: {
              valid,
              empty,
              dirty: true,
            },
          }));
        });
      });
    }
  }, [scriptLoaded]);
  const { token: tokenError, ...errors } = formik.errors;
  const isFormValid = !Object.keys(errors).length;
  const shouldRequestToken =
    microformInstanceRef.current &&
    cardNumberValidation.valid &&
    securityCodeValidation.valid &&
    !formik.errors?.[CYBERSOURCE_FIELD_EXPIRY_DATE] &&
    !formik.values.token &&
    isFormValid &&
    formik.isSubmitting;
  useEffect(() => {
    if (shouldRequestToken) {
      dispatch(startLoading());
      const creditCardExpiryDateObject = new Date(formik.values[CYBERSOURCE_FIELD_EXPIRY_DATE]);
      const expirationMonth = (creditCardExpiryDateObject.getMonth() + 1)
        .toString()
        .padStart(2, '0');
      const expirationYear = creditCardExpiryDateObject.getFullYear().toString();
      // @ts-expect-error TODO(flow-migration): microformInstanceRef.current is defined at this point
      microformInstanceRef.current.createToken(
        {
          expirationMonth,
          expirationYear,
        },
        (err, tokenizedCard) => {
          if (err) {
            dispatch(endLoading());
            throw Error(err.message || 'Could not generate token');
          }

          formik.setFieldValue('token', tokenizedCard);
          setTimeout(formik.submitForm, 0);
        },
      );
    }

    /**
     * If we have a token and the form payment process is on error, we must delete the old token
     * because the user could update his credit card data, so we must request a new token on the next submit
     */
    const shouldDeleteToken =
      formik.values.token && formik.status && formik.status.paymentProcessOnError;

    if (shouldDeleteToken) {
      formik.setFieldValue('token', '');
    }
  }, [formik.isSubmitting, formik.status]);
  const inputStyle =
    'outline-none rounded-2 text-14 text-black duration-250 ease-in transition-all w-full';
  const [monthExpiryPlaceholder, yearExpiryPlaceholder] = t(
    'common.payment.hipay.placeholderExpiryDate',
  )
    .toUpperCase()
    .split('/');
  return (
    <Fragment>
      <FormikInput type="hidden" isRequired name="token" />
      <Title
        className={classnames('mb-10', {
          'formik-errored': !formik.values.token,
        })}
        theme="title-brand-n2"
      >
        {t('common.payment.hipay.cardDetailTitle')}
      </Title>
      <Capsule header="none">
        <div className="flex flex-wrap px-0 py-15">
          <HostedField
            label={t('common.payment.hipay.cardNumberLabel')}
            id={`${CYBERSOURCE_FIELD_CARD_NUMBER}`}
            {...hostedFieldsState.number}
          />
          <div className="flex w-full flex-col sm:flex-row">
            <HostedField
              label={t('common.payment.hipay.expiryDateLabel')}
              id={`${CYBERSOURCE_FIELD_EXPIRY_DATE}`}
              small
              hasError={formik.errors?.[CYBERSOURCE_FIELD_EXPIRY_DATE]}
              isValid={
                formik.touched?.[CYBERSOURCE_FIELD_EXPIRY_DATE] &&
                !formik.errors?.[CYBERSOURCE_FIELD_EXPIRY_DATE]
              }
              container={false}
            >
              <FormikInput
                type="creditCardExpiry"
                name={`${CYBERSOURCE_FIELD_EXPIRY_DATE}`}
                className="w-full"
                dateFormat={t('booking.form.dateFormat')}
                monthPlaceholder={monthExpiryPlaceholder}
                yearPlaceholder={yearExpiryPlaceholder}
                validate={creditCardExpiryValidator}
                customClasses={inputStyle}
              />
            </HostedField>
            <HostedField
              label={t('common.payment.hipay.cvcLabel')}
              id={`${CYBERSOURCE_FIELD_CVC}`}
              small
              {...hostedFieldsState.securityCode}
            />
          </div>
        </div>
      </Capsule>
    </Fragment>
  );
};
